import { Component, OnInit, AfterViewInit } from '@angular/core';
import { ProductService } from '../../services/productservice';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../environments/environment';

interface DownloadItem {
  title: string;
  body: string;
  field_images: string;
  nid: string;
  field_pdf: string;
  field_downloads_category: string;
}

interface DownloadTab {
  label: string; // translation key shown on the tab
  categories: string[]; // API field_downloads_category value(s) this tab matches;
}

@Component({
  selector: 'downloads',
  templateUrl: './downloads.component.html',
  styleUrls: ['./downloads.component.scss'],
})
export class DownloadsComponent implements OnInit, AfterViewInit {
  // Banner / galleria
  public downloadsDetails: any = { bannerImage: null, bannerTitle: null, content: null };
  images: any = [];
  activeIndex: any;
  displayCustom: boolean;

  // Data
  categoryData: DownloadItem[] = [];

  /*
    DESIGN PREVIEW ONLY.
    Set useMockData = true to render the 4 placeholder cards from the PDF
    (no API needed). Set false to use the real getDownloadBrochure() data.
    Swap field_images for real cover assets if you want the previews to look final.
  */
  useMockData: boolean = false;
  mockCards: DownloadItem[] = [
    {
      field_downloads_category: 'Flyers',
      title: 'Seychelles Niche Flyer',
      body: "Explore the main island's beaches, culture and hidden gems.",
      field_images: 'assets/images/NoImage.jpg',
      nid: 'mock-1',
      field_pdf: '',
    },
    {
      field_downloads_category: 'Maps',
      title: 'Seychelles Niche Flyer',
      body: "Explore the main island's beaches, culture and hidden gems.",
      field_images: 'assets/images/NoImage.jpg',
      nid: 'mock-2',
      field_pdf: '',
    },
    {
      field_downloads_category: 'Events',
      title: 'Seychelles Niche Flyer',
      body: "Explore the main island's beaches, culture and hidden gems.",
      field_images: 'assets/images/NoImage.jpg',
      nid: 'mock-3',
      field_pdf: '',
    },
    {
      field_downloads_category: 'Safety Guides',
      title: 'Seychelles Niche Flyer',
      body: "Explore the main island's beaches, culture and hidden gems.",
      field_images: 'assets/images/NoImage.jpg',
      nid: 'mock-4',
      field_pdf: '',
    },
  ];

  /*
    Each tab maps to one or more API category values (field_downloads_category).
    >>> ADJUST the `categories` arrays to match your real Drupal category names. <<<
    current API categories are:
      Event, Map, Safety, Walks & Trails, Snorkeling & Diving, Brochure
  */
  tabs: DownloadTab[] = [
    { label: 'All Resources', categories: [] },
    { label: 'Brochures', categories: ['Brochure'] },
    { label: 'Travel Guides', categories: ['Walks & Trails', 'Snorkeling & Diving'] }, // <-- confirm mapping
    { label: 'Maps', categories: ['Map'] },
    { label: 'Safety Guides', categories: ['Safety'] },
  ];
  activeTabLabel: string = 'All Resources';

  // Search / paging
  searchText: string = '';

  // ----- Autocomplete (PrimeNG p-autoComplete) -----
  // `downloadapi` returns the full list in one call, so suggestions are filtered
  // from the already-loaded categoryData — no extra request per keystroke.
  selectedItem: any = '';
  suggestions: DownloadItem[] = [];
  featuredItem: DownloadItem | null = null;
  featuredViewerUrl: string = ''; // API stream URL for the inline featured viewer
  filteredResults: DownloadItem[] = [];
  displayedResults: DownloadItem[] = [];
  displayLimit: number = 8; // cards shown before "View all resources"
  showAllResources: boolean = false;

  // PDF shown in the flipbook modal — set per card, streamed through API.
  flipbookPdf: string = '';
  showFlipbook: boolean = false;
  flipbookTitle: string = '';

  constructor(public productService: ProductService, private route: ActivatedRoute) {}

  ngOnInit(): void {
    this.getAboutUsDetails();
    this.getBrochures();

    // In design-preview mode show the placeholder cards right away
    if (this.useMockData) {
      this.featuredItem = this.mockCards[0];
      this.featuredViewerUrl = '';
      this.applyFilters();
    }

    // Scroll-to-section support
    this.route.fragment.subscribe((fragment) => {
      if (fragment) {
        setTimeout(() => {
          const element = document.getElementById(fragment);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 500);
      }
    });
  }

  // Build the API URL that streams a given Drupal PDF
  private toViewerUrl(pdfUrl: string): string {
    if (!pdfUrl) return '';
    const lang = (localStorage.getItem('language') || 'en').toLowerCase();
    return environment.apiSearchBaseURL + 'downloadpdf/' + lang + '?url=' + encodeURIComponent(pdfUrl);
  }

  getAboutUsDetails() {
    this.productService.getDowloadsDetails().subscribe((data) => {
      if (data.field_banner_image && data.field_banner_image.length > 0) {
        this.downloadsDetails.bannerImage = data.field_banner_image[0].url;
        data.field_banner_image.forEach((item: any) => {
          this.images.push(item.url);
        });
      }
      if (data.field_banner_title && data.field_banner_title.length > 0) {
        this.downloadsDetails.bannerTitle = this.cleanHtmlEntities(data.field_banner_title[0].value);
      }
      if (data.body && data.body.length > 0) {
        this.downloadsDetails.content = this.cleanHtmlEntities(data.body[0].value);
      }
    });
  }

  private toDrupalAbsolute(url: string): string {
    if (!url) return '';
    if (url.startsWith('http')) return url; // already absolute (staging)
    const host = environment.imageOldurl.replace(/\/sites\/default\/files\/?$/, '');
    return url.startsWith('/') ? host + url : url; // prepend admin host for relative paths
  }

  getBrochures() {
    this.productService.getDownloadBrochure().subscribe((data) => {
      this.categoryData = data.results.map((item: any) => ({
        ...item,
        field_downloads_category: this.cleanHtmlEntities(item.field_downloads_category),
        title: this.cleanHtmlEntities(item.title),
        body: this.cleanHtmlEntities(item.body),
        field_images: this.toDrupalAbsolute(item.field_images),
        field_pdf: this.toDrupalAbsolute(item.field_pdf),
      }));

      // Featured publication is STATIC: always the Islands Brochure (Niche Flyer).
      // Match by title, fall back to the first Brochure, then the first item.
      this.featuredItem =
        this.categoryData.find((i) => i.title === 'Islands Brochure') ||
        this.categoryData.find((i) => i.field_downloads_category === 'Brochure') ||
        this.categoryData[0] ||
        null;
      this.featuredViewerUrl = this.featuredItem ? this.toViewerUrl(this.featuredItem.field_pdf) : '';

      this.applyFilters();
    });
  }

  cleanHtmlEntities(str: string): string {
    if (!str) return '';
    return str
      .replace(/\\u0026amp;/g, '&')
      .replace(/&amp;/g, '&')
      .replace(/&#039;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/\\u003C/g, '<')
      .replace(/\\u003E/g, '>')
      .replace(/\\u0022/g, '"')
      .replace(/\\u0027/g, "'");
  }

  // ----- Autocomplete handlers -----

  // Called by p-autoComplete as the user types
  searchDownloads(event: any) {
    const query = ((event && event.query) || '').toLowerCase().trim();
    if (!query) {
      this.suggestions = [];
      return;
    }
    this.suggestions = this.categoryData.filter(
      (i) =>
        (i.title && i.title.toLowerCase().includes(query)) ||
        (i.field_downloads_category && i.field_downloads_category.toLowerCase().includes(query))
    );
  }

  // Called when the user picks a suggestion (PrimeNG 11 emits the item itself)
  onSelectDownload(item: any) {
    if (!item || !item.title) {
      return;
    }
    this.selectedItem = item;
    this.searchText = item.title;
    this.showAllResources = true; // show the match even if it sits past the display limit
    this.applyFilters();
  }

  // Fires on every model change — value is a string while typing, an object once selected
  onSearchChange(value: any) {
    this.selectedItem = value;
    this.searchText = typeof value === 'string' ? value : (value && value.title) || '';
    this.showAllResources = false;
    this.applyFilters();
  }

  clearSearch() {
    this.selectedItem = '';
    this.searchText = '';
    this.suggestions = [];
    this.showAllResources = false;
    this.applyFilters();
  }

  // ----- Tabs / filtering / search -----

  selectTab(tab: DownloadTab) {
    this.activeTabLabel = tab.label;
    this.showAllResources = false;
    this.applyFilters();
  }

  applyFilters() {
    const activeTab = this.tabs.find((t) => t.label === this.activeTabLabel);

    // Use placeholder cards while previewing the design, real data otherwise
    let results = this.useMockData ? this.mockCards : this.categoryData;

    // Tab filter (empty categories = All Resources). Skipped in mock preview.
    if (!this.useMockData && activeTab && activeTab.categories.length > 0) {
      results = results.filter((i) => activeTab.categories.includes(i.field_downloads_category));
    }

    // Search filter
    const term = (this.searchText || '').trim().toLowerCase();
    if (term) {
      results = results.filter(
        (i) =>
          (i.title && i.title.toLowerCase().includes(term)) ||
          (i.body && i.body.toLowerCase().includes(term)) ||
          (i.field_downloads_category && i.field_downloads_category.toLowerCase().includes(term))
      );
    }

    this.filteredResults = results;
    this.updateDisplayed();
  }

  // (Featured publication is static — set once in getBrochures, not changed by tabs.)

  updateDisplayed() {
    this.displayedResults = this.showAllResources
      ? this.filteredResults
      : this.filteredResults.slice(0, this.displayLimit);
  }

  viewAllResources() {
    this.showAllResources = true;
    this.updateDisplayed();
  }

  // ----- Flipbook modal -----

  openFlipbook(item: DownloadItem | null) {
    if (!item || !item.field_pdf) {
      return; // nothing to render
    }
    this.flipbookPdf = this.toViewerUrl(item.field_pdf); // stream through the API
    this.flipbookTitle = item.title || '';
    this.showFlipbook = true;
    document.body.style.overflow = 'hidden';
  }

  closeFlipbook() {
    this.showFlipbook = false;
    document.body.style.overflow = '';
  }

  // ----- Download / banner helpers -----

  saftyPdf(nid: any, field_pdf: any) {
    const url = this.toDrupalAbsolute(field_pdf);
    const link = document.createElement('a');
    link.target = '_blank';
    link.href = url;
    link.click();
  }

  imageClick(index: number) {
    this.activeIndex = index;
    this.displayCustom = true;
  }

  scollBottom() {
    window.scrollTo({ top: 540, behavior: 'smooth' });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const input = document.querySelector('.downloads-search input') as HTMLInputElement;

      if (input) {
        input.setAttribute('role', 'combobox');
        input.setAttribute('aria-haspopup', 'listbox');
      }
    });

    const input = document.querySelector('.downloads-search input') as HTMLInputElement;

    if (!input) {
      return;
    }

    const activeId = input.getAttribute('aria-activedescendant');

    if (activeId && !document.getElementById(activeId)) {
      input.removeAttribute('aria-activedescendant');
    }
  }
}
